const SDK_URL = 'https://verify.msg91.com/otp-provider.js';
const SDK_SCRIPT_ID = 'msg91-otp-widget-sdk';
const SDK_TIMEOUT_MS = 10000;

let sdkLoadPromise = null;
let initializedWidgetKey = '';

const waitFor = (predicate, timeoutMs = SDK_TIMEOUT_MS, timeoutMessage = 'MSG91 OTP service did not become ready') => new Promise((resolve, reject) => {
  const startedAt = Date.now();
  const check = () => {
    if (predicate()) {
      resolve();
      return;
    }
    if (Date.now() - startedAt >= timeoutMs) {
      reject(new Error(timeoutMessage));
      return;
    }
    window.setTimeout(check, 50);
  };
  check();
});

const loadSdk = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('MSG91 OTP requires a browser'));
  }
  if (typeof window.initSendOTP === 'function') return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise((resolve, reject) => {
    let settled = false;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      callback(value);
    };
    const timeoutId = window.setTimeout(
      () => finish(reject, new Error('MSG91 OTP service did not load in time')),
      SDK_TIMEOUT_MS,
    );
    const loaded = () => finish(resolve);
    const failed = () => finish(reject, new Error('Failed to load MSG91 OTP service'));
    const existing = document.getElementById(SDK_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', loaded, { once: true });
      existing.addEventListener('error', failed, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SDK_SCRIPT_ID;
    script.src = SDK_URL;
    script.async = true;
    script.onload = loaded;
    script.onerror = failed;
    document.head.appendChild(script);
  }).catch((error) => {
    // Allow a later user action to retry after a transient CDN/network failure.
    sdkLoadPromise = null;
    throw error;
  });
  return sdkLoadPromise;
};

const formatProviderError = (error, fallback) => {
  if (typeof error === 'string' && error.trim()) return error;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  if (typeof error?.data?.message === 'string' && error.data.message.trim()) return error.data.message;
  return fallback;
};

const invokeWidgetMethod = (
  methodName,
  leadingArgs,
  trailingArgs,
  fallbackError,
) => new Promise((resolve, reject) => {
  const method = window[methodName];
  if (typeof method !== 'function') {
    reject(new Error('MSG91 OTP service is unavailable'));
    return;
  }
  method.call(
    window,
    ...leadingArgs,
    (result) => resolve(result || {}),
    (error) => {
      const message = formatProviderError(error, fallbackError);
      // Provider payloads can contain identifiers/tokens. Log only a fixed
      // category and operation, never the payload or credentials.
      console.warn('[OTP widget] Request rejected', {
        operation: methodName,
        reason: /captcha/i.test(message) ? 'captcha_rejected' : 'provider_rejected',
      });
      reject(new Error(message));
    },
    ...trailingArgs,
  );
});

export const isMsg91WidgetProvider = (config) => config?.otp_provider === 'msg91_widget';

export const normalizeMsg91Identifier = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  // GalaGo's backend treats bare 10-digit numbers as Indian numbers.
  return digits.length === 10 ? `91${digits}` : digits;
};

export const initializeMsg91Widget = async (config) => {
  if (!isMsg91WidgetProvider(config)) return;
  if (!config?.widget_configured || !config?.widget_id || !config?.widget_token) {
    throw new Error('MSG91 OTP Widget is not configured');
  }

  const widgetKey = `${config.widget_id}:${config.widget_token}`;
  await loadSdk();
  await waitFor(() => typeof window.initSendOTP === 'function');

  if (initializedWidgetKey !== widgetKey || typeof window.sendOtp !== 'function') {
    window.initSendOTP({
      widgetId: config.widget_id,
      tokenAuth: config.widget_token,
      exposeMethods: true,
      success: () => {},
      failure: () => {},
    });
    initializedWidgetKey = widgetKey;
  }

  await waitFor(() => (
    typeof window.sendOtp === 'function'
    && typeof window.verifyOtp === 'function'
    && typeof window.retryOtp === 'function'
  ));

  // MSG91 exposes its methods before getWidgetProcess finishes. Until that
  // response arrives, sendOtp can treat CAPTCHA as disabled and send no token.
  // Wait for configuration, then let the SDK perform its invisible CAPTCHA;
  // isCaptchaVerified() stays false before that send and is not a readiness gate.
  try {
    await waitFor(() => (
      typeof window.getWidgetData === 'function'
      && window.getWidgetData()?.widgetId === config.widget_id
    ), SDK_TIMEOUT_MS, 'Phone verification is still loading. Please try again.');
  } catch (error) {
    console.warn('[OTP widget] Initialization incomplete', { reason: 'configuration_not_ready' });
    throw error;
  }
};

const extractValue = (result, keys) => {
  if (typeof result === 'string' && result.trim()) return result.trim();
  if (!result || typeof result !== 'object') return '';
  for (const key of keys) {
    const value = result[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return extractValue(result.data, keys);
};

export const sendMsg91WidgetOtp = async (config, phone) => {
  await initializeMsg91Widget(config);
  const identifier = normalizeMsg91Identifier(phone);
  if (!identifier) throw new Error('A valid phone number is required');

  const result = await invokeWidgetMethod(
    'sendOtp',
    [identifier],
    [],
    'MSG91 could not send the OTP',
  );
  const requestId = extractValue(result, ['reqId', 'requestId', 'request_id', 'message']);
  if (!requestId) throw new Error('MSG91 did not return an OTP request ID');
  return { requestId, result };
};

export const retryMsg91WidgetOtp = async (config, requestId) => {
  await initializeMsg91Widget(config);
  if (!requestId) throw new Error('The OTP request has expired; send a new OTP');
  return invokeWidgetMethod(
    'retryOtp',
    [config?.widget_retry_channel || '12'],
    [requestId],
    'MSG91 could not resend the OTP',
  );
};

export const verifyMsg91WidgetOtp = async (config, code, requestId) => {
  await initializeMsg91Widget(config);
  if (!requestId) throw new Error('The OTP request has expired; send a new OTP');
  const result = await invokeWidgetMethod(
    'verifyOtp',
    [Number(code)],
    [requestId],
    'MSG91 could not verify the OTP',
  );
  const accessToken = extractValue(
    result,
    ['access-token', 'access_token', 'accessToken', 'token', 'message'],
  );
  if (!accessToken) throw new Error('MSG91 did not return a verification token');
  return { accessToken, result };
};
